/**
 * The project's Code tab, machine by machine (AIOE 0.36.0): which machines hold each repository, and which can take the
 * project's work. Both read `roots[].holders` from `GET /projects/v1/:id`.
 */

import type { DeviceOrderBatch, NamedUnapprovedServers, NodeAssignment, ProjectRoot, ProjectRootHolder, RootAgentFiles } from '@aioe/shared';
import { Cpu } from '@gravity-ui/icons';
import { Dropdown, Label } from '@heroui/react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { computeRoutes, routes } from '../api/client.ts';
import { DeployNodeSheet } from './compute/DeployNodeSheet.tsx';
import { usePermissions } from '../permissions.ts';

import { timeAgo, useLoad } from '../hooks.ts';
import { VIEW_TONES } from './ui/Art.tsx';
import { KIND_TEXT, MENU_KINDS, type OrderKind } from './nodeOrders.ts';
import { AskDialog, OrderBatch } from './nodeOrdersPanel.tsx';
import { Button, Empty, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';

export type RootWithHolders = ProjectRoot & { holders?: ProjectRootHolder[] };

const ORDER_KINDS = MENU_KINDS;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Who a machine belongs to, in words: "your JP-laptop", "Con's Con-laptop" is just the name with the person after it, a node is its own name. */
function nameOf(h: ProjectRootHolder, me?: string): string {
  if (h.kind === 'node') return h.deviceName;
  return h.owner && me && h.owner.id === me ? `your ${h.deviceName}` : h.deviceName;
}

/** Whether the machine can take work now: online with its host running. */
const ready = (h: ProjectRootHolder) => h.online && h.hostUp !== false;

/** The state of a machine in words: "WSL Ubuntu-26.04 · stopped 14 min ago", "running · 2 agents busy". */
function stateWords(h: ProjectRootHolder): string {
  const parts: string[] = [];
  if (h.hostUp === false) parts.push(`${h.distro ? `WSL ${h.distro} · ` : ''}stopped ${timeAgo(h.lastSeenAt)}`);
  else if (!h.online) parts.push(`offline, last seen ${timeAgo(h.lastSeenAt)}`);
  else parts.push('running');
  if (ready(h) && h.agents) parts.push(`${plural(h.agents, 'agent', 'agents')} busy`);
  return parts.join(' · ');
}

const markOf = (h: ProjectRootHolder) => (!ready(h) ? 'offline' : h.agents ? 'working' : 'online');

function MachineLink({ h, me }: { h: ProjectRootHolder; me?: string }) {
  return (
    <Link to={h.kind === 'node' ? `/nodes?node=${encodeURIComponent(h.deviceId)}` : `/workbenches/${encodeURIComponent(h.deviceId)}`} className="font-medium text-accent hover:underline">
      {nameOf(h, me)}
    </Link>
  );
}

/** How many machines a list shows before "Show all": enough to see who has it, few enough to read at a glance. */
const PREVIEW = 5;

/** Order for a short list: your own first, then machines working, running, offline; by name within each. */
function holderRank(h: ProjectRootHolder, me?: string): number {
  if (h.owner && me && h.owner.id === me) return 0;
  if (!ready(h)) return 3;
  return h.agents ? 1 : 2;
}

/** "12 machines: 3 working, 6 running, 3 offline", counting only what is there. */
function tally(holders: ProjectRootHolder[]): string {
  const working = holders.filter((h) => ready(h) && h.agents).length;
  const running = holders.filter((h) => ready(h) && !h.agents).length;
  const offline = holders.length - working - running;
  const parts = [working ? `${working} working` : '', running ? `${running} running` : '', offline ? `${offline} offline` : ''].filter(Boolean);
  return `${plural(holders.length, 'machine', 'machines')}: ${parts.join(', ')}`;
}

/**
 * A list that stays readable at any size: up to PREVIEW rows, then "Show all N", which opens the whole list with a
 * search field in a box that scrolls on its own. The caller sorts; `text` is what the search matches.
 */
export function ScalingList<T>({ items, keyOf, text, render, noun, className = 'grid gap-1 text-sm' }: { items: T[]; keyOf: (t: T) => string; text: (t: T) => string; render: (t: T) => ReactNode; noun: string; className?: string }) {
  const [all, setAll] = useState(false);
  const [query, setQuery] = useState('');
  if (items.length <= PREVIEW) return <ul className={className}>{items.map((t) => <li key={keyOf(t)}>{render(t)}</li>)}</ul>;
  if (!all)
    return (
      <div className="grid gap-1">
        <ul className={className}>{items.slice(0, PREVIEW).map((t) => <li key={keyOf(t)}>{render(t)}</li>)}</ul>
        <div>
          <Button size="sm" variant="ghost" className="h-auto min-h-0 px-0 text-xs text-accent" onPress={() => setAll(true)}>
            Show all {items.length} {noun}
          </Button>
        </div>
      </div>
    );
  const q = query.trim().toLowerCase();
  const shown = q ? items.filter((t) => text(t).toLowerCase().includes(q)) : items;
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${items.length} ${noun}`} aria-label={`Search ${noun}`} className={`${inputClass} max-w-xs`} autoFocus />
        <Button size="sm" variant="ghost" onPress={() => { setAll(false); setQuery(''); }}>
          Show fewer
        </Button>
      </div>
      {shown.length ? (
        <ul className={`${className} max-h-80 overflow-y-auto pr-1`}>{shown.map((t) => <li key={keyOf(t)}>{render(t)}</li>)}</ul>
      ) : (
        <p className="text-xs text-muted">No {noun} match.</p>
      )}
    </div>
  );
}

/** What a holder is called and how it is doing, as one searchable string. */
const holderText = (h: ProjectRootHolder, me?: string) => [nameOf(h, me), h.kind === 'node' ? 'node' : h.owner?.name ?? '', stateWords(h)].join(' ');

/**
 * Every machine that holds a repository: a count by state when there are many, then the machines, yours first. For a
 * node's clone the owners can let members attach to it (`attach`), shown on that node's own line.
 */
export function RepositoryMachines({ holders, me, attach }: { holders?: ProjectRootHolder[]; me?: string; attach?: { canonical: (nodeId: string) => boolean; clone: (nodeId: string) => boolean; onAllow?: (nodeId: string) => void } }) {
  if (!holders?.length) return <p className="text-xs text-muted">No machine reports this repository right now.</p>;
  const sorted = [...holders].sort((a, b) => holderRank(a, me) - holderRank(b, me) || nameOf(a, me).localeCompare(nameOf(b, me)));
  return (
    <div className="grid gap-1">
      {holders.length > PREVIEW ? <p className="text-xs text-muted tabular-nums">{tally(holders)}</p> : null}
      <ScalingList
        items={sorted}
        noun="machines"
        keyOf={(h) => h.deviceId}
        text={(h) => holderText(h, me)}
        render={(h) => (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="inline-flex items-center gap-1.5">
              <span className="status-dot" data-state={markOf(h)} aria-hidden="true" />
              <MachineLink h={h} me={me} />
            </span>
            <span className="text-xs text-muted">
              {h.kind === 'node' ? 'node · ' : h.owner && h.owner.id !== me ? `${h.owner.name} · ` : ''}
              {stateWords(h)}
            </span>
            {attach?.clone(h.deviceId) ? (
              attach.canonical(h.deviceId) ? (
                <span className="text-xs text-muted">· members may attach</span>
              ) : attach.onAllow ? (
                <Button size="sm" variant="ghost" className="h-auto min-h-0 px-0 text-xs text-accent" onPress={() => attach.onAllow!(h.deviceId)}>
                  Let members attach
                </Button>
              ) : null
            ) : null}
          </div>
        )}
      />
    </div>
  );
}

export interface Machine {
  deviceId: string;
  holder?: ProjectRootHolder;
  assignment?: NodeAssignment;
  repositories: string[];
}

/** Compute: every machine that can take this project's work, assigned nodes and members' workbenches that hold a repository. */
/** Every machine that can take the project's work: assigned nodes, then members' workbenches holding a repository, each once. */
export function computeMachines(roots: RootWithHolders[], compute: NodeAssignment[]): Machine[] {
  const machines = new Map<string, Machine>();
  for (const a of compute) machines.set(a.nodeId, { deviceId: a.nodeId, assignment: a, repositories: [] });
  const seen = new Set<string>();
  for (const r of roots) {
    if (seen.has(r.rootId)) continue;
    seen.add(r.rootId);
    for (const h of r.holders ?? []) {
      const m = machines.get(h.deviceId) ?? { deviceId: h.deviceId, repositories: [] };
      m.holder = h;
      m.repositories.push(r.name ?? r.rootId);
      machines.set(h.deviceId, m);
    }
  }
  return [...machines.values()];
}

export function ProjectCompute({ projectId, owner, roots, compute, me }: { projectId: string; owner?: boolean; roots: RootWithHolders[]; compute: NodeAssignment[]; me?: string }) {
  const tone = VIEW_TONES.projects!;
  const navigate = useNavigate();
  const [kind, setKind] = useState<OrderKind>();
  const [batch, setBatch] = useState<DeviceOrderBatch>();
  const [deploying, setDeploying] = useState(false);
  // "Deploy a node" (D447) is offered with roles on, when an approved cluster with a node pool serves this project.
  const perms = usePermissions();
  const served = useLoad(() => (perms.data?.mode === 'roles' ? computeRoutes.approved().then((a) => a.clusters.filter((c) => (c.projectIds ?? []).includes(projectId) && (c.nodePools?.length ?? 0) > 0)).catch(() => []) : Promise.resolve([])), [perms.data?.mode, projectId]);
  const machines = computeMachines(roots, compute);
  // An assigned node that holds no repository has no holder line to name it: ask the nodes list, only then.
  const unnamed = machines.some((m) => !m.holder);
  const nodeNames = useLoad(() => (unnamed ? routes.nodes().then((ns) => Object.fromEntries(ns.map((n) => [n.deviceId, n.hostname] as const))).catch(() => ({}) as Record<string, string>) : Promise.resolve({} as Record<string, string>)), [unnamed]);
  // What the assigned nodes report, for "fits on 3 of 4 nodes" in the model dialog; a caller who cannot read the fleet gets no line.
  const nodeFacts = useLoad(() => (kind === 'model.download' ? routes.devices().catch(() => []) : Promise.resolve([])), [kind]);
  const names: Record<string, string> = { ...(nodeNames.data ?? {}), ...Object.fromEntries(roots.flatMap((r) => (r.holders ?? []).map((h) => [h.deviceId, h.deviceName] as const))) };
  /** Never the id: a node not yet named reads as such. */
  const nameFor = (m: Machine) => m.holder?.deviceName ?? names[m.deviceId] ?? 'A node not yet reporting';
  // Assigned nodes first, then machines working, running, offline; by name within each.
  const rank = (m: Machine) => (m.assignment ? 0 : m.holder ? holderRank(m.holder) : 3);
  const rows = machines.sort((a, b) => rank(a) - rank(b) || nameFor(a).localeCompare(nameFor(b)));
  return (
    <>
    <ListGroup
      title="Compute"
      count={rows.length || undefined}
      description="Machines that can take this project's work: the nodes assigned to it, then members' workbenches that hold one of its repositories."
      empty={rows.length === 0 ? <Empty tone={tone} icon={<Cpu />} compact title="No machine yet" body="No machine holds a repository of this project yet, and no node is assigned to it. Assign a node from Nodes, or open a repository on a workbench." /> : undefined}
      actions={
        <Dropdown>
          <Button size="sm" variant="secondary">
            Nodes
          </Button>
          <Dropdown.Popover placement="bottom end" className="min-w-52">
            <Dropdown.Menu
              aria-label="Nodes of this project"
              onAction={(key) => {
                if (key === 'assign') navigate('/nodes');
                else if (key === 'deploy') setDeploying(true);
                else setKind(String(key) as OrderKind);
              }}
            >
              <Dropdown.Item id="assign" textValue="Assign a node">
                <Label>Assign a node</Label>
              </Dropdown.Item>
              {(served.data?.length ?? 0) > 0
                ? [
                    <Dropdown.Item key="deploy" id="deploy" textValue="Deploy a node">
                      <Label>Deploy a node</Label>
                    </Dropdown.Item>,
                  ]
                : []}
              {owner && rows.some((m) => m.assignment)
                ? ORDER_KINDS.map((k) => (
                    <Dropdown.Item key={k} id={k} textValue={`Ask every node to ${KIND_TEXT[k].toLowerCase()}`}>
                      <Label>Ask every node: {KIND_TEXT[k].toLowerCase()}</Label>
                    </Dropdown.Item>
                  ))
                : []}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      }
    >
      {rows.length > PREVIEW ? <ListBlock className="text-xs text-muted tabular-nums">{tally(rows.flatMap((m) => (m.holder ? [m.holder] : [])))}{rows.some((m) => m.assignment) ? `; ${plural(rows.filter((m) => m.assignment).length, 'assigned node', 'assigned nodes')}` : ''}</ListBlock> : null}
      {rows.length ? (
        <ListBlock flush>
          <ScalingList
            items={rows}
            noun="machines"
            className="list-group list-group--bare m-0 list-none p-0"
            keyOf={(m) => m.deviceId}
            text={(m) => [nameFor(m), m.holder ? stateWords(m.holder) : '', m.holder?.owner?.name ?? '', m.repositories.join(' ')].join(' ')}
            render={(m) => {
              const h = m.holder;
              const kind = m.assignment ? 'Assigned node' : h?.owner && me && h.owner.id === me ? 'Your workbench' : h?.owner ? `${h.owner.name}'s workbench` : "A member's workbench";
              const quota = m.assignment ? [m.assignment.capabilities.join(', '), m.assignment.quota?.maxConcurrentAgents ? `up to ${m.assignment.quota.maxConcurrentAgents} agents` : ''].filter(Boolean).join(' · ') : '';
              const repos = m.repositories.length > 3 ? `holds ${m.repositories.slice(0, 3).join(', ')} and ${m.repositories.length - 3} more` : m.repositories.length ? `holds ${m.repositories.join(', ')}` : 'holds no repository of this project';
              return (
                <ListRow
                  title={
                    h ? (
                      <MachineLink h={h} me={me} />
                    ) : (
                      <Link to={`/nodes?node=${encodeURIComponent(m.deviceId)}`} className="font-medium text-accent hover:underline">
                        {nameFor(m)}
                      </Link>
                    )
                  }
                  description={[kind, h ? stateWords(h) : '', repos, quota].filter(Boolean).join(' · ')}
                  status={h ? <StatusMark state={markOf(h)} label={!ready(h) ? (h.hostUp === false ? 'Stopped' : 'Offline') : h.agents ? 'Working' : 'Running'} /> : undefined}
                />
              );
            }}
          />
        </ListBlock>
      ) : null}
    </ListGroup>
      {kind && (
        <AskDialog
          kind={kind}
          targets={machines.filter((m) => m.assignment).map((m) => {
            const f = nodeFacts.data?.find((d) => d.deviceId === m.deviceId)?.facts;
            return { name: nameFor(m), hardware: f?.hardware, runner: f?.runner };
          })}
          onClose={() => setKind(undefined)}
          onSend={async (k, body) => {
            setBatch(await routes.orderProjectNodes(projectId, k, body));
          }}
        />
      )}
      {batch?.batchId && <OrderBatch key={batch.batchId} projectId={projectId} batchId={batch.batchId} initial={batch} names={names} onClose={() => setBatch(undefined)} />}
    {deploying && served.data?.length ? <DeployNodeSheet projectId={projectId} clusters={served.data} onClose={() => setDeploying(false)} /> : null}
    </>
  );
}

/** What a repository carries for agents, in one sentence (AIOE 0.40.0); `not approved` leads to the Tools page. Nothing to say until a workbench reports it. */
export function RepositoryAgentFiles({ files }: { files?: RootAgentFiles }) {
  if (!files) return <p className="text-xs text-muted">No agent files reported yet; AI Workbench 0.80 and later report them.</p>;
  const unapproved = files.mcpServers.filter((m) => !m.governed).length;
  const parts = [files.instructions ? 'instructions present' : 'no instructions', plural(files.hooks, 'hook', 'hooks'), plural(files.skills, 'skill', 'skills')];
  if (files.agents) parts.push(plural(files.agents, 'agent', 'agents'));
  return (
    <p className="text-xs text-muted" title={`Read by ${files.reportedBy.deviceName} ${timeAgo(files.checkedAt)}`}>
      Agent files: {parts.join(', ')}
      {files.setup ? `; set up for agents ${timeAgo(files.setup.at)}` : ''}
      {files.mcpServers.length > 0 && (
        <>
          ; names {plural(files.mcpServers.length, 'server', 'servers')}
          {unapproved > 0 ? (
            <>
              {' '}
              ({unapproved} the organisation has{' '}
              <Link to="/tools" className="text-accent hover:underline">
                not approved
              </Link>
              )
            </>
          ) : (
            ', all approved'
          )}
        </>
      )}
    </p>
  );
}

/** Servers repositories name that the organisation has not published (the Tools page): the name, and the repositories that name it. */
export function NamedByRepositories({ data }: { data: NamedUnapprovedServers }) {
  return (
    <ScalingList
      items={data.servers}
      noun="servers"
      keyOf={(s) => `${s.kind}/${s.name}`}
      text={(s) => `${s.name} ${s.repositories.map((r) => r.name).join(' ')}`}
      render={(s) => (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 px-4 py-1.5">
          <span className="font-medium">{s.name}</span>
          <span className="text-xs text-muted">{s.repositories.slice(0, 3).map((r) => r.name).join(', ')}{s.repositories.length > 3 ? ` and ${s.repositories.length - 3} more` : ''}</span>
        </div>
      )}
      className="grid divide-y divide-separator text-sm"
    />
  );
}
